import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow, PageHero } from "@/components/Reveal";

const capabilities = [
  {
    title: "Deployment readiness evaluation",
    body: "Before an AI system is put into production, it should be independently assessed against the claims made for it. Reform Intelligence provides structured, repeatable evaluation of whether a system performs as stated, where it fails, and what it should not be trusted to do.",
  },
  {
    title: "Reliability drift measurement",
    body: "Models do not stay the same. Their behavior shifts as inputs, data, and conditions change. Reform Intelligence measures that drift over time, so degradation and emerging failure modes are caught before they cause harm.",
  },
  {
    title: "Agent governance and containment",
    body: "Autonomous systems that take actions on their own raise a different class of risk. Reform Intelligence develops the standards and controls that keep agentic systems bounded, observable, and accountable.",
  },
  {
    title: "Governance integration",
    body: "Evaluation is only useful if it reaches the people who make decisions. Reform Intelligence builds evaluation evidence into the enterprise risk, audit, and compliance systems that boards and regulators already rely on.",
  },
];

export default function WhatWeDo() {
  useSeo(
    "What We Do | Reform Intelligence®",
    "The evaluation and governance layer that sits upstream of every serious AI deployment: frameworks, instruments, and standards institutions use to deploy AI they can trust."
  );

  return (
    <>
      <PageHero
        eyebrow="What we do"
        headline="The layer that makes AI accountable."
        subhead="Reform Intelligence operates upstream of AI deployment, providing the evidence institutions need to trust the systems they rely on."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>The evaluation and governance layer</Eyebrow>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#1A1A1A]">
              Reform Intelligence does not build AI. It builds the evaluation and governance layer that sits upstream of every serious AI deployment: the frameworks, instruments, and standards institutions use to measure readiness, detect drift, and govern autonomous systems. This is the layer that turns AI from an unmanaged risk into an accountable capability.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="capabilities-section" className="border-y border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>Capabilities</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-px border border-[#E4E7EC] bg-[#E4E7EC] md:grid-cols-2">
            {capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="bg-white">
                <div data-testid={`capability-block-${i}`} className="h-full p-8 sm:p-10">
                  <span className="font-serif2 text-sm font-semibold text-[#B08D3F]">0{i + 1}</span>
                  <h3 className="mt-3 font-serif2 text-xl font-semibold text-[#1F2D4E]">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#5A6B8C]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Our approach</Eyebrow>
              <h2 className="mt-4 font-serif2 text-2xl font-semibold text-[#1F2D4E] sm:text-3xl">Measurement science, not machine learning.</h2>
              <p className="mt-6 leading-relaxed text-[#1A1A1A]">
                Our discipline is measurement science, not machine learning. Our methods are transparent, auditable, and methodology-first, so a result can be examined and trusted rather than taken on faith. We preserve our independence by holding no equity or ownership stake in any company we evaluate, publishing our methodology so it can be reproduced and challenged, and pricing our work the same regardless of the result. That independence is what gives our evidence its weight.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-[#E4E7EC] bg-[#F5F6F8] p-8 sm:p-10">
                <Eyebrow>How to start</Eyebrow>
                <p className="mt-4 leading-relaxed text-[#1A1A1A]">
                  Institutions typically begin with a research advisory engagement or a pilot evaluation program. Both are designed to deliver value quickly while establishing the standards a longer relationship is built on.
                </p>
                <Link
                  to="/contact"
                  data-testid="how-to-start-cta"
                  className="mt-8 inline-block bg-[#1F2D4E] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F]"
                >
                  Request a briefing
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
